import { useCallback, useEffect, useRef, useState, type Ref } from 'react'
import { useOptionalDeviceCache, type DeviceCacheApi } from '../device-cache/context'
import { useInstallPrompt } from '../hooks/useInstallPrompt'
import { useTranslation } from '../i18n/I18nProvider'
import { useAppUpdate } from '../pwa/useAppUpdate'
import { Icon } from './icons/Icon'
import { scrollShellToTop } from './shellScroller'
import { Wordmark } from './Wordmark'

/**
 * `useTranslation('home')` inside a P6 file (home-layout-handoff.md §2): the Home-only button
 * labels live in `home.json` because they name a P7 screen's controls, but only `AppBar` itself
 * renders them — there is no other seam. Logged in `implementation-notes.md` → P7.
 */

/**
 * **One control with three positions, not two toggles with four combinations.**
 *
 * `grid`/`spacious` still arrive as the pair Home stores (`layoutPreference.ts` says why the
 * stored shape did not change), but the bar no longer offers a way to set them independently:
 * `onCycle` advances to the next mode in `HOME_LAYOUT_CYCLE` and is the only thing either boolean
 * answers to. The fourth combination — a large list — is not reachable and no longer exists as an
 * option; the two density labels that used to name a knob of its own went with it.
 */
export interface AppBarLayoutControl {
  readonly grid: boolean
  readonly spacious: boolean
  readonly onCycle: () => void
}

export interface AppBarSortControl {
  readonly onDone: () => void
}

/** D247 — the back bar's trailing action. See `AppBar`'s `action` prop. */
export interface AppBarAction {
  readonly label: string
  onClick(): void
  readonly disabled?: boolean
}

/**
 * How long a refresh must run before the title says so, and how long it says so once it starts.
 * The delay is what keeps a warm refresh silent; the floor is what stops the one that outlives it
 * from flashing.
 */
const BUSY_AFTER_MS = 200
const BUSY_MIN_MS = 400

/**
 * The screen title, and — wherever a refresh means anything — the app's one manual
 * reconcile-with-the-server control (D178).
 *
 * **It replaced a glyph, and the glyph is the reason.** The bar used to carry a rotating refresh
 * button whose spin was driven straight off `cache.refreshing`, so it reported *every* refresh: the
 * boot fetch, a resume, a reconnect. What a resident saw on opening the app was an icon spinning at
 * them about work they had not asked for, which is the whole of the complaint D178 answers. The
 * control did not need a better animation — it needed to stop speaking unbidden.
 *
 * **So this reads the user's own tap, never `cache.refreshing`.** A background refresh now shows no
 * chrome at all; that is deliberate, and it is the one thing to understand before "restoring" an
 * indicator here. Wiring this state to the context flag would rebuild the original defect in a
 * quieter costume.
 *
 * **A tap does two things, because they are one intent** — *give me this screen, current, from the
 * top.* {@link scrollShellToTop} first, so the fresh list arrives where it can be read, then
 * `refreshAll()`.
 *
 * **`refreshAll()`, not `refresh()`.** D146 names this control in the decision itself: it is the
 * user-initiated way back from a demoted status reading to a verified one, so it is the list read
 * *and* the probe sweep. D142 leaves exactly two callers of that cycle — D128's reconnect and the
 * one a user asks for — and this is now the second. Narrowing it to the list alone breaks a
 * decision rather than a convenience.
 *
 * **Read from the context rather than taken as a prop**, for the same reason `useShellUpdate()` is:
 * the alternative is threading a callback through nine P7/P6 screens for a concern none of them
 * own. `useOptionalDeviceCache()` (not `useDeviceCache()`) because the bar renders above the data
 * layer — with no provider the title is inert text, which is the correct degradation for chrome and
 * the wrong one for a screen.
 *
 * **The accepted cost is discoverability.** A tappable title does not announce itself the way an
 * icon does. It is reachable on every screen that had the button, the iOS status-bar tap covers the
 * scrolling half by platform convention, and no surface in the app is now dependent on finding it —
 * boot, resume and reconnect all refresh on their own.
 */
function BarTitle({
  title,
  interactive,
  wordmark = false,
}: {
  readonly title: string
  readonly interactive: boolean
  /**
   * **Draw the name instead of setting it (D201) — and this prop is where that boundary lives.**
   *
   * It is passed by exactly one of `AppBar`'s three returns: the bare menu bar, which is Home's
   * and only Home's (`HomeScreen` is the sole `onMenu` caller). The other two must not have it.
   * A back bar's title is a gate's name, and a `.title-only` bar's is install's post-provisioning
   * step name — Hebrew copy, not a brand — so dressing either as the wordmark would put the
   * product's name where the subject's name belongs.
   *
   * That boundary used to be `:not(.back-only):not(.title-only)` in `global.css`, and the second
   * `:not()` was the load-bearing one, because a title-only bar also matches the first. It is a
   * prop now because the mark is markup rather than a font: the stylesheet can re-dress a string
   * but it cannot replace one with a drawing. The CSS rule that dressed the string is gone with
   * it, so the boundary is stated once, here, rather than in two places that have to agree.
   */
  readonly wordmark?: boolean
}): JSX.Element {
  const cache = useOptionalDeviceCache()
  const { t: tCommon } = useTranslation('common')
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])

  // Nothing here may outlive the bar: a screen change unmounts it mid-refresh routinely, and both
  // timers below call `setBusy`.
  useEffect(
    () => () => {
      for (const timer of timers.current) clearTimeout(timer)
      timers.current = []
    },
    [],
  )

  const onTap = useCallback((target: DeviceCacheApi): void => {
    if (inFlight.current) return
    inFlight.current = true
    scrollShellToTop()

    let shownAt: number | undefined
    const show = setTimeout(() => {
      shownAt = Date.now()
      setBusy(true)
    }, BUSY_AFTER_MS)
    timers.current.push(show)

    void target.refreshAll().finally(() => {
      clearTimeout(show)
      inFlight.current = false
      if (shownAt === undefined) return
      const hide = setTimeout(() => setBusy(false), Math.max(0, BUSY_MIN_MS - (Date.now() - shownAt)))
      timers.current.push(hide)
    })
  }, [])

  // The mark carries the same string the text did, so the accessible name is unchanged whichever
  // shape this is: the `<button>` labels itself below, and the `<span>` is named by the `<title>`
  // inside the svg.
  const content = wordmark ? <Wordmark label={title} /> : title

  if (!interactive || cache === undefined) return <span className="abtitle">{content}</span>
  return (
    <button
      type="button"
      className={`abtitle${busy ? ' busy' : ''}`}
      onClick={() => onTap(cache)}
      aria-busy={busy}
      aria-label={`${title} — ${tCommon('refresh')}`}
    >
      {content}
    </button>
  )
}

/**
 * Ported from `prototype/screens/shared.js`'s `appBar()`. Either a Home-style bar (menu +
 * title + optional layout/sort control) or a detail-style bar (back + title), never both —
 * callers pass `onBack` to get the latter.
 */
export function AppBar({
  title,
  onMenu,
  onBack,
  action,
  hamburgerPulse = false,
  layout,
  sort,
  menuButtonRef,
}: {
  readonly title: string
  readonly onMenu?: () => void
  readonly onBack?: () => void
  /**
   * D247 — a trailing action on the **back bar only**. `PreferencesScreen`/`PersonalizeScreen`
   * render it as Save; **D346 gives it a second consumer** — `InviteScreen`'s address-book phase,
   * where it reads *Add (N)* and reaches `addFromBook`. Both are the same slot: a label, a
   * click handler and a disabled flag, with no notion of which screen or which verb it is.
   *
   * It is documented back-only the way `BarTitle`'s `wordmark` is documented Home-only: the menu
   * bar already owns its inline end (`.edits-inline`), and the `title-only` bar exists precisely
   * to offer no control at all, so an action there would undo the defect it was created to fix.
   *
   * Rendered bare, with no wrapper. `.edits-inline` groups Home's *pair* of controls; one button
   * needs no group, and chrome that outlives its reason is the pattern `global.css` records twice
   * on this bar already. Placement is free: `.back-only .abtitle`'s `margin-inline-end: auto`
   * pushes whatever follows the title to the inline end.
   */
  readonly action?: AppBarAction | undefined
  readonly hamburgerPulse?: boolean
  readonly layout?: AppBarLayoutControl
  readonly sort?: AppBarSortControl
  readonly menuButtonRef?: Ref<HTMLButtonElement>
}): JSX.Element {
  const { t: tCommon } = useTranslation('common')
  const { t: tHome } = useTranslation('home')
  /**
   * **The dot means "a row in the sidebar wants you", not any one thing.**
   *
   * Three reasons raise it now: `hamburgerPulse` (the caller's own), an available update, and an
   * app that can still be added to the home screen. They share one indicator because the specific
   * meaning lives on the row inside; two dots on one button would say less, not more.
   *
   * **The update reason is two rails and one question (D266).** `useAppUpdate` reports a waiting
   * service worker in a browser and a newer store binary in the container, and this reads the
   * answer rather than the rail — a resident neither knows nor cares which one is behind the dot.
   *
   * **It is also the one thing the mute silences, and `available` is the field that carries it.**
   * A resident who turned app updates off on *My account* gets no dot; the sidebar's Update row
   * stays, because that row is the container's only route to the store, and `App` reads `present`
   * for it. **This is exactly one dot either way** — `updateAvailable` folds both rails, so the
   * two can never raise two.
   *
   * The install reason is why the card no longer has to repeat. It is shown once, on the sign-in
   * screen, and this is what carries the offer from then on — a standing reminder that costs a
   * glance rather than a tap, and that ends by itself the moment the app is installed
   * (`canInstall` folds in `isStandalone`).
   *
   * Both are read here rather than passed in, so no caller has to thread them: `AppBar` is P6's
   * and the screens that render it are P7's, and this would otherwise be a prop change in someone
   * else's files for a shell concern. A second `useInstallPrompt()` instance beside `App`'s is
   * cheap and correct — `beforeinstallprompt` is dispatched on `window`, so every mounted listener
   * captures it, and only `App`'s copy ever calls `promptInstall()`.
   */
  const { available: updateAvailable } = useAppUpdate()
  const { canInstall } = useInstallPrompt()

  if (onBack !== undefined) {
    // Fix E/F: the back button carries only the chevron + "back" word — never the screen
    // title, which used to be swallowed inside the button and read like the button's own
    // label ("Back My gates"). The title stays a *sibling* of `.back` — that is the part D178
    // does not get to change when it makes the title itself a button.
    //
    // `.abtitle`'s `margin-inline-end: auto` outlives the refresh button it used to pin to the
    // inline end: it is now what keeps the title beside `.back` rather than centred in the space
    // the button vacated.
    return (
      <div className="app-bar back-only">
        <button type="button" className="back" onClick={onBack} aria-label={tCommon('back')}>
          <Icon name="chevron-left" size={24} />
          {tCommon('back')}
        </button>
        <BarTitle title={title} interactive />
        {action === undefined ? null : (
          <button
            type="button"
            className="bar-action"
            disabled={action.disabled ?? false}
            onClick={action.onClick}
          >
            {action.label}
          </button>
        )}
      </div>
    )
  }

  /**
   * Neither control. A step that must not be left — install's post-provisioning screens — used to
   * get the menu bar anyway, rendering a hamburger with no `onClick`: a button that looks like the
   * way out of a screen and does nothing when pressed. Rendering the title alone is the honest
   * bar for that case, and `.title-only` restores the block size the hamburger was pinning.
   */
  if (onMenu === undefined) {
    return (
      <div className="app-bar title-only">
        {/* Inert, as the refresh button was absent here: these are install's post-provisioning
            steps, and a sweep of every gate's status is not what a half-finished install wants. */}
        <BarTitle title={title} interactive={false} />
      </div>
    )
  }

  return (
    <div className="app-bar">
      <button
        ref={menuButtonRef}
        className="hamburger"
        onClick={onMenu}
        type="button"
        aria-label={tCommon('menu')}
      >
        <Icon name="menu" size={30} />
        {hamburgerPulse || updateAvailable || canInstall ? <span className="pulse-dot" /> : null}
      </button>
      {/*
        Inert in reorder mode, for the reason the layout button is absent there: a refresh
        replaces the gate list wholesale (`device-cache.md` step 6), and doing that under a drag
        pulls the tile out from under the finger.
      */}
      <BarTitle title={title} interactive={sort === undefined} wordmark />
      <div className="edits-inline">
        {sort !== undefined ? (
          <button className="edit-done" type="button" onClick={sort.onDone}>
            {tCommon('close')}
          </button>
        ) : layout !== undefined ? (
          // **One bare button, and the wrapper is gone with the outline it carried.** The three
          // modes are positions of a single "how gates are shown" control, so there was nothing
          // left for a group to group: the `<div>` survived the pair it was built for by two
          // rounds, first as an outlined pill and then as a circle. What is left is the same kind
          // of thing the hamburger opposite it is — an icon button on the bar — and it shares
          // that rule in `global.css` rather than resembling it.
          <button
            className="view-control"
            type="button"
            onClick={layout.onCycle}
            // The label names the DESTINATION while the glyph names the current mode — the
            // split the pair already had, kept because a rotating control that announced where
            // it already is would say nothing about what the tap does.
            aria-label={
              layout.spacious
                ? tHome('switchToListLayout')
                : layout.grid
                  ? tHome('switchToLargeGridLayout')
                  : tHome('switchToGridLayout')
            }
          >
            <Icon
              name={layout.grid ? (layout.spacious ? 'layout-template' : 'layout-grid') : 'layout-list'}
              // **25, and both numbers it sits between were shipped first.** What a bare glyph is
              // read against is its neighbour's INK, not its box: `size={30}` matched the two svg
              // boxes exactly (38.1px each) and still read larger, because `menu` draws 16x12 of
              // the 24-unit viewBox while all three `layout-*` glyphs draw 18x18 — 1.5x the ink
              // height at the same box. Matching ink outright is 30 x 12/18 = 20, and that
              // overshot: a square mark whose ink is exactly as tall as a three-line stack reads
              // SMALLER than it, because the stack is 33% wider and the eye is adding the two
              // axes. 25 is the half-step, at ~1.25x the hamburger's ink height. The three glyphs
              // share one ink box, so the cycle cannot make the control jump.
              //
              // The stroke does not follow the size: `Icon.tsx` hard-codes `strokeWidth={2}` in
              // viewBox units, so a smaller box renders a thinner line, and two bare glyphs at
              // different weights is the same mismatch through another property. `global.css`
              // restates it at `2.4` for this glyph, which is the hamburger's 3.17px exactly —
              // `size x stroke` is the invariant, and it stays 60 whenever this number moves.
              size={25}
            />
          </button>
        ) : null}
      </div>
    </div>
  )
}
